import { NativeStackHeaderProps } from '@react-navigation/native-stack';
import { getHeaderTitle } from '@react-navigation/elements';
import React from 'react';
import { useSafeGoBack } from './useSafeGoBack';
import IconButton from '../IconButton';
import Text from '../Text';
import HeaderStack from './HeaderStack';
import Stack from '../Stack';
import MaxWidthRestrictedContent from '../MaxWidthRestrictedContent';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import { MaybeRenderHeaderInHeaderPortal } from '../../shared/MaybeSideNavLayout';
import { useMaybeLoadingAppType } from '../../../hooks/useAppType';
import OrganizationButton from './OrganizationButton';
import { StyleProp, View, ViewStyle } from 'react-native';

export type BaseHeaderOwnProps = {
    hideBackButton?: boolean;
    leftContent?: React.ReactNode;
    centerContent?: React.ReactNode;
    desktopContent?: React.ReactNode;
    rightContent?: React.ReactNode;
    fullWidth?: boolean;
    height: number;
    style?: StyleProp<ViewStyle>;
};

export type BaseHeaderProps = BaseHeaderOwnProps & NativeStackHeaderProps;

const BaseHeader: React.FC<BaseHeaderProps> = (props) => {
    const isMobile = useIsMobileLayout();
    const isProApp = useMaybeLoadingAppType() === 'professional';
    const { goBack } = useSafeGoBack(props);
    const getTitle = () => getHeaderTitle(props.options, props.route.name) || '';

    if (!isMobile && !props.desktopContent) {
        return null;
    }

    const renderRightContent = () => {
        if (props.rightContent) {
            return props.rightContent;
        } else if (isProApp && !isMobile) {
            return <OrganizationButton />;
        } else {
            return null;
        }
    };

    const fullWidthContent = (
        <MaybeRenderHeaderInHeaderPortal>
            <HeaderStack noBorder={!isMobile}>
                {isMobile ? (
                    <>
                        <Stack horizontal style={{ flex: 2, justifyContent: 'flex-start', alignItems: 'center' }}>
                            {props.hideBackButton ? null : (
                                <IconButton
                                    variant="header-back-button"
                                    icon="ChevronLeft"
                                    onPress={goBack}
                                    testID="header-go-back"
                                />
                            )}
                            {props.leftContent}
                        </Stack>
                        <Stack
                            horizontal
                            style={{
                                flex: 6,
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            {props.centerContent ? (
                                props.centerContent
                            ) : (
                                <Text variant="base-header-title" alignText="center">
                                    {getTitle()}
                                </Text>
                            )}
                        </Stack>
                        <Stack
                            horizontal
                            space={2}
                            style={{ flex: 2, justifyContent: 'flex-end', alignItems: 'center' }}
                        >
                            {renderRightContent()}
                        </Stack>
                    </>
                ) : (
                    props.desktopContent
                )}
            </HeaderStack>
        </MaybeRenderHeaderInHeaderPortal>
    );

    return (
        <View style={{ width: '100%' }}>
            {props.fullWidth || !isMobile ? (
                fullWidthContent
            ) : (
                <MaxWidthRestrictedContent>{fullWidthContent}</MaxWidthRestrictedContent>
            )}
        </View>
    );
};

export default React.memo(BaseHeader);
